<template>
  <div v-if="available" class="eudr-references">
    <button type="button" class="btn btn-outline-secondary" @click="openReferences">
      {{ buttonLabel }}<span v-if="savedCount"> · {{ savedCount }} saved</span>
    </button>
    <BModal
      :id="`eudr-${item.id}`"
      v-model="isOpen"
      title="EUDR references"
      no-footer
      :no-close-on-backdrop="saving"
      :no-close-on-esc="saving"
      :no-header-close="saving"
    >
      <div class="eudr-references">
        <p>
          Add a due diligence statement (DDS) reference only for goods that need one. Use the reference from your
          declaration or supplier.
        </p>
        <p v-if="!isDhl" class="alert alert-warning">
          References are saved with this shipment. Electronic transfer is currently supported only for DHL Express.
        </p>
        <p class="small">
          This form supports DDS references. Simplified declarations and exemption document codes are not supported yet.
        </p>
        <form novalidate @submit.prevent="saveReferences">
          <fieldset v-for="(product, index) in products" :key="index" class="eudr-references__product">
            <legend>{{ index + 1 }}. {{ product.description || 'Customs item' }}</legend>
            <p class="small">Commodity code: {{ product.commodityCode || 'Not provided' }}</p>
            <label :for="`dds-${item.id}-${index}`">DDS reference (only if applicable)</label>
            <input
              :id="`dds-${item.id}-${index}`"
              v-model="references[index]"
              type="text"
              class="form-control"
              maxlength="35"
              autocomplete="off"
              :readonly="!canEdit || saving"
              :aria-invalid="Boolean(errors[index])"
              :aria-describedby="errors[index] ? `dds-error-${item.id}-${index}` : undefined"
              @input="clearError(index)"
            />
            <p v-if="errors[index]" :id="`dds-error-${item.id}-${index}`" class="text-danger" role="alert">
              {{ errors[index] }}
            </p>
          </fieldset>
          <p v-if="error" class="text-danger" role="alert">{{ error }}</p>
          <p v-if="!canEdit" class="small">References cannot be changed after booking has started.</p>
          <div class="eudr-references__actions">
            <button v-if="canEdit" type="submit" class="btn create-btn btn-green" :disabled="saving">
              {{ saving ? 'Saving…' : 'Save references' }}
            </button>
            <button type="button" class="btn btn-outline-secondary" :disabled="saving" @click="closeReferences">
              {{ canEdit ? 'Cancel' : 'Close' }}
            </button>
          </div>
        </form>
      </div>
    </BModal>
    <p v-if="saved" class="small mt-2" role="status">References saved.</p>
  </div>
</template>

<script lang="ts">
import type { CN22, CN22Product, Item } from '@/types/Models/Item'
import { BModal } from 'bootstrap-vue-next'
import { computed, defineComponent, ref, type ComputedRef, type PropType, type Ref } from 'vue'
import { DHL_EXPRESS } from '@/const/Carriers'
import ItemsProxy from '@/proxies/ItemsProxy'

export default defineComponent({
  name: 'EudrReferences',
  components: { BModal },
  props: {
    item: { type: Object as PropType<Item>, required: true },
    editable: { type: Boolean, default: false },
    buttonLabel: { type: String, default: 'EUDR references' },
  },
  emits: ['saved'],
  setup(props, { emit }) {
    const isOpen: Ref<boolean> = ref(false)
    const saving: Ref<boolean> = ref(false)
    const saved: Ref<boolean> = ref(false)
    const references: Ref<string[]> = ref([])
    const errors: Ref<string[]> = ref([])
    const error: Ref<string> = ref('')
    const products: ComputedRef<CN22Product[]> = computed(() => props.item.cn22?.products || [])
    const available: ComputedRef<boolean> = computed(
      () => props.item.eudrAvailable === true && products.value.length > 0,
    )
    const isDhl: ComputedRef<boolean> = computed(() => props.item.carrierHandle === DHL_EXPRESS)
    const canEdit: ComputedRef<boolean> = computed(() => props.editable && !props.item.shipmentNumber)
    const savedCount: ComputedRef<number> = computed(
      () => products.value.filter((product) => product.eudr?.ddsReference).length,
    )

    const openReferences = (): void => {
      references.value = products.value.map((product) => product.eudr?.ddsReference || '')
      errors.value = []
      error.value = ''
      saved.value = false
      isOpen.value = true
    }

    const closeReferences = (): void => {
      if (!saving.value) isOpen.value = false
    }

    const clearError = (index: number): void => {
      errors.value[index] = ''
      error.value = ''
    }

    const saveReferences = (): void => {
      if (saving.value || !canEdit.value || !available.value || !props.item.cn22) return
      errors.value = references.value.map((value) =>
        value && !/^[A-Za-z0-9]{1,35}$/.test(value) ? 'Use 1–35 letters or digits, without spaces.' : '',
      )
      if (errors.value.some(Boolean)) return
      const cn22: CN22 = {
        ...props.item.cn22,
        products: products.value.map((product, index) => ({
          ...product,
          eudr: references.value[index] ? { ddsReference: references.value[index] } : null,
        })),
      }
      saving.value = true
      error.value = ''
      new ItemsProxy()
        .update(props.item.id, { cn22 })
        .then(() => {
          saved.value = true
          isOpen.value = false
          emit('saved')
        })
        .catch(() => {
          error.value = 'Could not save the references. Your changes are still here. Check the shipment and try again.'
        })
        .finally(() => {
          saving.value = false
        })
    }

    return {
      available,
      isDhl,
      canEdit,
      products,
      savedCount,
      isOpen,
      saving,
      saved,
      references,
      errors,
      error,
      openReferences,
      closeReferences,
      clearError,
      saveReferences,
    }
  },
})
</script>
